import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Autocomplete, CircularProgress, Chip } from '@mui/material';
import debounce from 'lodash/debounce';

interface AsyncMultiSearchTextBoxProps<T> {
    disabled?: boolean;
    fetchOptions: (query: string) => Promise<T[]>; // The API function prop
    getOptionLabel: (option: T) => string;
    isOptionEqualToValue: (option: T, value: T) => boolean;
    onChange: (value: T[]) => void;
    value: T[];
}

export default function AsyncMultiSearchTextBox<T>({ disabled = false, fetchOptions, getOptionLabel, isOptionEqualToValue, onChange, value, }: AsyncMultiSearchTextBoxProps<T>) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<readonly T[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);

    const debouncedFetch = useMemo(
        () =>
            debounce(async (query: string) => {
                setLoading(true);
                try {
                    const results = await fetchOptions(query);
                    // Filter out any result that is already present in the 'value' array
                    const filteredResults = results.filter(result => !value.some((selected) => isOptionEqualToValue(result, selected)));
                    // Merge current selections with only the unique new results
                    setOptions([...value, ...filteredResults]);
                } catch (error) {
                    console.error("Search error:", error);
                } finally {
                    setLoading(false);
                }
            }, 500),
        [fetchOptions, isOptionEqualToValue, value]
    );

    useEffect(() => {
        let active = true;
        if (inputValue === '') {
            setOptions(value);
            return;
        }

        if (active) debouncedFetch(inputValue);

        return () => { active = false; };
    }, [inputValue, debouncedFetch, value]);

    return (
        <Autocomplete<T, true, false, false>
            ChipProps={{ color: "info" }} size="small" clearOnEscape
            multiple
            disabled={disabled}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={options}
            loading={loading}
            value={value}
            onChange={(_e, newValue) => onChange(newValue)}
            onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
            filterOptions={(x) => x}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined" color="primary"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip color='info' key={key} label={getOptionLabel(option)} {...tagProps} />;
                })
            }
        />
    );
}
